import { useEffect, useRef, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import api from "@/lib/api";
import BountyBadge from "@/components/BountyBadge";
import { toEmbedUrl } from "@/lib/video";

export default function Star() {
    const { search } = useLocation();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeIdx, setActiveIdx] = useState(0);
    const containerRef = useRef(null);
    const itemRefs = useRef([]);

    useEffect(() => {
        api.get("/star/feed")
            .then((r) => setItems(r.data.items || []))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        if (!containerRef.current) return;
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting && e.intersectionRatio > 0.6) {
                        const idx = parseInt(e.target.dataset.idx, 10);
                        setActiveIdx(idx);
                    }
                });
            },
            { root: containerRef.current, threshold: [0.6] }
        );
        itemRefs.current.forEach((el) => el && observer.observe(el));
        return () => observer.disconnect();
    }, [items]);

    if (loading) return <div className="py-24 text-center text-[#A3A3A3]">Loading the Star…</div>;
    if (!items.length) return <div className="py-24 text-center text-[#A3A3A3]">The Star is dark — no signal yet.</div>;

    return (
        <div data-testid="star-page" className="mx-auto max-w-[440px] px-4 py-6">
            <div className="mb-4">
                <div className="caption text-[var(--brand-accent)]">Sovereign Star</div>
                <h1 className="mt-1 font-serif text-3xl tracking-display text-[#FAFAFA]">Live Signal</h1>
                <p className="mt-1 caption text-[#737373]">Swipe vertically · {items.length} signals</p>
            </div>

            <div
                ref={containerRef}
                data-testid="star-feed"
                className="snap-y snap-mandatory overflow-y-auto h-[78vh] rounded-md border border-white/5"
                style={{ scrollBehavior: "smooth" }}
            >
                {items.map((it, idx) => {
                    const video = it.intro_video_url ? toEmbedUrl(it.intro_video_url) : null;
                    const isActive = activeIdx === idx;
                    return (
                        <div
                            key={`${it.type}-${it.id}-${idx}`}
                            ref={(el) => (itemRefs.current[idx] = el)}
                            data-idx={idx}
                            data-testid={`star-card-${it.type}-${it.id}`}
                            className={`relative snap-start flex flex-col justify-end h-[78vh] p-7 transition-all duration-700 overflow-hidden ${isActive ? "scale-100" : "scale-[0.98]"}`}
                            style={{
                                background: it.type === "privateer"
                                    ? "linear-gradient(180deg, rgba(0,0,0,0.4) 0%, rgba(20,20,20,0.95) 70%, #0A0A0A 100%)"
                                    : "linear-gradient(180deg, rgba(201,169,110,0.06) 0%, rgba(20,20,20,0.95) 70%, #0A0A0A 100%)",
                            }}
                        >
                            {video && isActive && (
                                <div className="absolute inset-0 z-0" data-testid={`star-video-${it.id}`}>
                                    {video.kind === "file" ? (
                                        <video
                                            src={video.embedUrl}
                                            autoPlay
                                            muted
                                            loop
                                            playsInline
                                            className="h-full w-full object-cover"
                                        />
                                    ) : (
                                        <iframe
                                            title={`intro-${it.id}`}
                                            src={`${video.embedUrl}?autoplay=1&mute=1&loop=1&controls=0&playsinline=1&modestbranding=1&rel=0`}
                                            className="absolute left-1/2 top-1/2 h-[150%] w-[150%] -translate-x-1/2 -translate-y-1/2 border-0"
                                            allow="autoplay; encrypted-media; picture-in-picture"
                                            allowFullScreen
                                        />
                                    )}
                                    <div
                                        className="absolute inset-0 pointer-events-none"
                                        style={{
                                            background: "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.0) 30%, rgba(0,0,0,0.85) 100%)",
                                        }}
                                    />
                                </div>
                            )}
                            <div className="absolute inset-0 grain pointer-events-none opacity-30" />
                            <div className="relative z-10">
                                <div className="caption" style={{ color: "var(--brand-accent)" }}>
                                    {it.tag}
                                    {it.mission_type ? ` · ${String(it.mission_type).toUpperCase()}` : ""}
                                    {it.niche && it.type === "privateer" ? ` · ${it.niche.toUpperCase()}` : ""}
                                </div>
                                <h2 className="mt-3 font-serif text-4xl leading-[0.95] tracking-display text-[#FAFAFA]">
                                    {it.title}
                                </h2>
                                <p className="mt-2 text-[#A3A3A3]">{it.subtitle}</p>
                                {it.skills?.length > 0 && (
                                    <div className="mt-4 flex flex-wrap gap-1.5">
                                        {it.skills.map((s) => (
                                            <span key={s} className="rounded-sm border border-white/10 bg-white/[0.03] px-2.5 py-1 text-xs text-[#A3A3A3]">{s}</span>
                                        ))}
                                    </div>
                                )}
                                {it.bounty > 0 && (
                                    <div className="mt-5">
                                        <BountyBadge amount={it.bounty} size="md" testId={`star-bounty-${it.id}`} />
                                    </div>
                                )}
                                {it.salary_max > 0 && (
                                    <p className="mt-3 font-mono text-xs text-[#737373]">
                                        up to ${(it.salary_max / 1000).toFixed(0)}k
                                    </p>
                                )}
                                <div className="mt-6">
                                    {it.type === "mission" && (
                                        <Link
                                            to={{ pathname: `/jobs/${it.id}`, search }}
                                            data-testid={`star-cta-${it.id}`}
                                            className="inline-flex items-center justify-center gap-2 rounded-sm px-6 py-3 text-sm font-medium transition-all duration-300"
                                            style={{ background: "var(--brand-accent)", color: "#0A0A0A" }}
                                        >
                                            View Mission →
                                        </Link>
                                    )}
                                    {it.type === "privateer" && (
                                        <Link
                                            to={{ pathname: "/foundry/interview", search }}
                                            className="inline-flex items-center justify-center gap-2 rounded-sm border border-white/10 px-6 py-3 text-sm font-medium text-[#FAFAFA] transition-all duration-300 hover:bg-white/5"
                                        >
                                            Become a Privateer →
                                        </Link>
                                    )}
                                </div>
                            </div>
                            <div className="absolute right-4 top-4 z-10 caption text-[#737373]">
                                {idx + 1} / {items.length}
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="mt-4 flex justify-center gap-1.5">
                {items.map((_, idx) => (
                    <span
                        key={idx}
                        className="h-1 w-6 rounded-full transition-all"
                        style={{ background: idx === activeIdx ? "var(--brand-accent)" : "rgba(255,255,255,0.1)" }}
                    />
                ))}
            </div>
        </div>
    );
}
